import React from "react";
import { Link } from "wouter";
import { SEOHead } from "@/components/SEOHead";
import { Header, Footer, Breadcrumbs } from "@/components/Navigation";
import { PRACTICE_INFO } from "@/site-data";
import {
  ShieldCheck,
  Award,
  GraduationCap,
  Heart,
  Calendar,
  Phone,
  CheckCircle,
  Building2,
  Stethoscope
} from "lucide-react";

export default function About() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "Physician",
    name: "Spence D. Harper, DPM",
    jobTitle: "Board-Certified Podiatric Physician & Surgeon",
    description: "Board-certified podiatric physician & surgeon with over 25 years of experience in surgical and conservative foot care at Wasatch Foot & Ankle Center.",
    image: "https://www.drspenceharper.com/manus-storage/dr-spence-harper_029434bc.png",
    telephone: "435-657-0329",
    address: {
      "@type": "PostalAddress",
      streetAddress: "190 N. Main Street",
      addressLocality: "Heber City",
      addressRegion: "UT",
      postalCode: "84032",
      addressCountry: "US"
    },
    alumniOf: [
      {
        "@type": "EducationalOrganization",
        name: "Temple University School of Podiatric Medicine",
        award: "Doctor of Podiatric Medicine, Graduated with Honors (1994)"
      },
      {
        "@type": "EducationalOrganization",
        name: "University of Utah",
        award: "Undergraduate Pre-Medical Studies"
      }
    ],
    memberOf: [
      {
        "@type": "Organization",
        name: "American Podiatric Medical Association (APMA)"
      },
      {
        "@type": "Organization",
        name: "Utah Podiatric Medical Association (UPMA)"
      },
      {
        "@type": "Organization",
        name: "American Board of Foot and Ankle Surgery (ABFAS)"
      }
    ]
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEOHead
        title="Meet Dr. Spence Harper, DPM | Board Certified Podiatrist Heber City UT"
        description="Learn about Dr. Spence D. Harper, board-certified podiatric surgeon with over 25 years of experience, Temple University honors graduate, and solo practitioner in Heber City, UT."
        canonicalPath="/about/"
        schema={aboutSchema}
      />
      <Header />
      <Breadcrumbs items={[{ label: "About Dr. Harper" }]} />

      <main className="flex-1">
        {/* HERO TITLE HEADER */}
        <section className="bg-[#0f2b48] text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-wider uppercase border border-cyan-400/30">
              <ShieldCheck className="w-4 h-4" /> Board-Certified Podiatric Physician &amp; Surgeon
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
              Meet Spence D. Harper, DPM
            </h1>
            <p className="text-slate-300 text-base sm:text-lg max-w-2xl mx-auto font-body">
              Providing empathetic, personalized foot and ankle medicine for over 25 years in Heber City and Wasatch County.
            </p>
          </div>
        </section>

        {/* MAIN BIOGRAPHY CONTENT */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              {/* Doctor Headshot Card */}
              <div className="lg:col-span-5">
                <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm space-y-6 sticky top-28">
                  <div className="w-48 h-48 sm:w-56 sm:h-56 mx-auto rounded-full overflow-hidden border-4 border-[#0284c7] shadow-lg">
                    <img
                      src={PRACTICE_INFO.doctorPhoto}
                      alt="Dr. Spence D. Harper, DPM"
                      loading="lazy"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  <div className="text-center space-y-1">
                    <h2 className="font-heading font-bold text-2xl text-[#0f2b48]">
                      Spence D. Harper, DPM
                    </h2>
                    <p className="text-sm font-semibold text-[#0284c7]">
                      Diplomate, American Board of Foot &amp; Ankle Surgery
                    </p>
                    <p className="text-xs text-slate-500">
                      Utah Medical License: 314751-0501
                    </p>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-slate-200 text-xs sm:text-sm text-slate-700">
                    <div className="flex items-center gap-3">
                      <GraduationCap className="w-5 h-5 text-[#0284c7] shrink-0" />
                      <span>Temple Univ. School of Podiatric Medicine (Honors, 1994)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Award className="w-5 h-5 text-[#0284c7] shrink-0" />
                      <span>Chief Surgical Resident, Sacred Heart Hospital (1996)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-[#0284c7] shrink-0" />
                      <span>Heber Valley Hospital &bull; Park City Hospital</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Stethoscope className="w-5 h-5 text-[#0284c7] shrink-0" />
                      <span>Former Podiatry Division Chairman, St. Mark's Hospital</span>
                    </div>
                  </div>

                  <div className="pt-4">
                    <Link
                      href="/appointments/"
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#0284c7] hover:bg-[#0369a1] text-white font-bold py-3 px-4 rounded-lg shadow transition-all"
                    >
                      <Calendar className="w-4 h-4" /> Schedule Visit with Dr. Harper
                    </Link>
                  </div>
                </div>
              </div>

              {/* In-depth Narrative */}
              <div className="lg:col-span-7 space-y-6 text-slate-700 leading-relaxed font-body text-base">
                <div>
                  <h3 className="font-heading text-2xl font-bold text-[#0f2b48] mb-3">
                    A Passion for Restoring Patient Mobility
                  </h3>
                  <p>
                    Spence D. Harper, DPM, is an exceptional board-certified podiatric physician and surgeon providing the highest caliber of specialized foot and ankle care at Wasatch Foot and Ankle Center in Heber City, Utah. With over two decades of clinical experience, Dr. Harper encourages a healthy, active lifestyle, emphasizes conservative non-surgical management whenever possible, and treats men, women, and children of all ages.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-2xl font-bold text-[#0f2b48] mb-3">
                    Rigorous Education &amp; Surgical Residency
                  </h3>
                  <p>
                    After completing his pre-medical undergraduate training at the <strong>University of Utah</strong>, Dr. Harper graduated with honors from <strong>Temple University School of Podiatric Medicine</strong> (formerly the Pennsylvania College of Podiatric Medicine) in Philadelphia, Pennsylvania, in 1994.
                  </p>
                  <p className="mt-3">
                    He completed an intensive two-year comprehensive foot and reconstructive ankle surgical residency in 1996 at <strong>Sacred Heart Hospital in Chicago, Illinois</strong>, where his clinical aptitude earned him the distinction of serving as <strong>Chief Resident</strong> during his final year.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-2xl font-bold text-[#0f2b48] mb-3">
                    Board Certification &amp; Research Contributions
                  </h3>
                  <p>
                    In 1996, Dr. Harper became a proud diplomate of the <strong>American Board of Foot and Ankle Surgery (ABFAS)</strong>. He also stays current with advanced treatment techniques for plantar fasciitis, chronic heel pain, and arthroscopic and endoscopic joint interventions.
                  </p>
                  <p className="mt-3">
                    To continually refine clinical treatments, Dr. Harper has conducted multiple clinical studies on bunion pain management and diabetic ulcerations, participating in clinical research trials with Jean Brown Research. He is an active member of both the American Podiatric Medical Association (APMA) and the Utah Podiatric Medical Association (UPMA).
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-2xl font-bold text-[#0f2b48] mb-3">
                    Independent Solo Practice Philosophy
                  </h3>
                  <p>
                    Dr. Harper believes deeply that medicine should remain personal. His solo practice is not owned by an impersonal hospital corporation or private equity group. This independence allows Dr. Harper to spend generous, unhurried time with every patient, answering questions thoroughly and fostering collaborative, lifelong relationships.
                  </p>
                </div>

                {/* Personal & Family Life */}
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 space-y-3">
                  <div className="flex items-center gap-2 text-[#0f2b48] font-heading font-bold text-lg">
                    <Heart className="w-5 h-5 text-rose-500" />
                    Life Beyond the Clinic
                  </div>
                  <p className="text-sm text-slate-600">
                    Away from the clinic, Dr. Harper is married with three beloved children. As an avid Utah outdoorsman, he loves fishing, camping, hunting, and riding all-terrain vehicles (ATVs) through the mountains with his family. You might also spot him tending his property on his tractor or cruising the Wasatch back roads in his vintage 1974 Toyota Land Cruiser.
                  </p>
                </div>

                <div className="pt-4 flex flex-col sm:flex-row gap-4">
                  <Link
                    href="/appointments/"
                    className="inline-flex items-center justify-center gap-2 bg-[#0284c7] hover:bg-[#0369a1] text-white font-bold px-6 py-3 rounded-lg shadow transition-all"
                  >
                    Request An Appointment <Calendar className="w-4 h-4" />
                  </Link>
                  <a
                    href={`tel:${PRACTICE_INFO.phone}`}
                    className="inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold px-6 py-3 rounded-lg transition-all"
                  >
                    <Phone className="w-4 h-4 text-[#0284c7]" /> Call (435) 657-0329
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
